import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { searchGlobal } from "@/api/search";
import { useAuth } from "@/auth/AuthProvider";
import {
  canReviewAllFlights,
  formatAccountTypeLabel,
  isAdminAccount,
} from "@/auth/roles";
import { useUiTheme } from "@/hooks/useUiTheme";

import {
  AdminIcon,
  AiIcon,
  AnalysisIcon,
  ChartIcon,
  FlightsIcon,
  HomeIcon,
  LogoutIcon,
  PilotsIcon,
  TrophyIcon,
  UploadIcon,
} from "./NavIcons";

interface PageEntry {
  title: string;
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
  roles?: Array<"pilot" | "instructor" | "admin">;
}

const PAGES: PageEntry[] = [
  { title: "Home", href: "/", Icon: HomeIcon },
  { title: "Performance", href: "/performance", Icon: ChartIcon },
  { title: "Leaderboards", href: "/leaderboards", Icon: TrophyIcon },
  { title: "Flights", href: "/flights", Icon: FlightsIcon },
  { title: "Flight compare", href: "/analysis", Icon: AnalysisIcon },
  { title: "Upload flight", href: "/flights/upload", Icon: UploadIcon },
  {
    title: "Pilots",
    href: "/users",
    Icon: PilotsIcon,
    roles: ["instructor", "admin"],
  },
  { title: "Admins", href: "/admins", Icon: AdminIcon, roles: ["admin"] },
  { title: "AI coach", href: "/ai-coach", Icon: AiIcon },
];

function filterPages(
  query: string,
  accountType: "pilot" | "instructor" | "admin",
): PageEntry[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return PAGES.filter(
    (p) =>
      (!p.roles || p.roles.includes(accountType)) &&
      p.title.toLowerCase().includes(q),
  ).slice(0, 5);
}

function formatFlightDate(start: string | null): string {
  if (!start) return "—";
  try {
    return new Date(start).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return start;
  }
}

export function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useUiTheme();
  const [themeMounted, setThemeMounted] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const userWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setThemeMounted(true);
  }, []);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
      if (
        userWrapRef.current &&
        !userWrapRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const debouncedQuery = useDebouncedValue(query, 220);
  const { data } = useQuery({
    queryKey: ["search", debouncedQuery],
    queryFn: () => searchGlobal(debouncedQuery),
    enabled: debouncedQuery.trim().length >= 1,
    staleTime: 10_000,
  });

  const pageResults = useMemo(
    () => (user ? filterPages(debouncedQuery, user.account_type) : []),
    [debouncedQuery, user],
  );

  const hasResults =
    pageResults.length > 0 ||
    (data && (data.flights.length || data.pilots.length || data.missions.length));

  if (!user) return null;

  const adminMode = isAdminAccount(user.account_type);
  const privilegedMode = canReviewAllFlights(user.account_type);

  const go = (href: string) => {
    setOpen(false);
    setQuery("");
    navigate(href);
  };

  return (
    <header className="topbar">
      <div className="topbar__search-wrap" ref={searchWrapRef}>
        <span className="topbar__search-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.5" y2="16.5" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          className="topbar__search-input"
          placeholder="Search flights, pilots, missions…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {query && (
          <button
            type="button"
            className="topbar__search-clear"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <kbd className="topbar__search-kbd">⌘K</kbd>

        {open && query.trim() && (
          <div className="topbar__search-dropdown">
            {!hasResults && (
              <p className="topbar__search-empty">No matches.</p>
            )}
            {pageResults.length > 0 && (
              <section className="topbar__search-section">
                <h5>Pages</h5>
                <ul>
                  {pageResults.map((page) => (
                    <li key={page.href}>
                      <button
                        type="button"
                        onClick={() => go(page.href)}
                        className="topbar__search-item"
                      >
                        <page.Icon />
                        <span>{page.title}</span>
                        <span className="topbar__search-kicker">Page</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {data && data.pilots.length > 0 && (
              <section className="topbar__search-section">
                <h5>Pilots</h5>
                <ul>
                  {data.pilots.map((pilot) => (
                    <li key={pilot.id}>
                      <button
                        type="button"
                        onClick={() => go(`/users/${pilot.id}`)}
                        className="topbar__search-item"
                      >
                        <PilotsIcon />
                        <span>{pilot.username}</span>
                        <span className="topbar__search-kicker">
                          {pilot.flight_count} flight
                          {pilot.flight_count === 1 ? "" : "s"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {data && data.flights.length > 0 && (
              <section className="topbar__search-section">
                <h5>Flights</h5>
                <ul>
                  {data.flights.map((flight) => (
                    <li key={flight.id}>
                      <button
                        type="button"
                        onClick={() => go(`/flights/${flight.id}`)}
                        className="topbar__search-item"
                      >
                        <FlightsIcon />
                        <span className="topbar__search-item-title">
                          {flight.session_name || flight.title}
                        </span>
                        <span className="topbar__search-kicker">
                          {formatFlightDate(flight.start_time)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {data && data.missions.length > 0 && (
              <section className="topbar__search-section">
                <h5>Missions</h5>
                <ul>
                  {data.missions.map((mission) => (
                    <li key={mission.name}>
                      <button
                        type="button"
                        onClick={() =>
                          go(
                            `/flights?mission=${encodeURIComponent(mission.name)}`,
                          )
                        }
                        className="topbar__search-item"
                      >
                        <TrophyIcon />
                        <span>{mission.name}</span>
                        <span className="topbar__search-kicker">
                          {mission.flight_count} flight
                          {mission.flight_count === 1 ? "" : "s"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>

      <div className="topbar__user-wrap" ref={userWrapRef}>
        {(adminMode || privilegedMode) && (
          <span
            className={`topbar__role-badge topbar__role-badge--${
              adminMode ? "admin" : "instructor"
            }`}
          >
            {formatAccountTypeLabel(user.account_type)}
          </span>
        )}

        <button
          type="button"
          className="topbar__theme"
          onClick={toggleTheme}
          aria-label="Switch theme"
          aria-pressed={themeMounted && theme === "light"}
          title={`Theme: ${theme === "light" ? "Light" : "Dark"}`}
        >
          {theme === "light" ? (
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="4" />
              <line x1="12" y1="2" x2="12" y2="5" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="2" y1="12" x2="5" y2="12" />
              <line x1="19" y1="12" x2="22" y2="12" />
              <line x1="4.2" y1="4.2" x2="6.3" y2="6.3" />
              <line x1="17.7" y1="17.7" x2="19.8" y2="19.8" />
              <line x1="4.2" y1="19.8" x2="6.3" y2="17.7" />
              <line x1="17.7" y1="6.3" x2="19.8" y2="4.2" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
            </svg>
          )}
        </button>

        <button
          type="button"
          className="topbar__user"
          onClick={() => setUserMenuOpen((v) => !v)}
          aria-expanded={userMenuOpen}
        >
          <span className="topbar__user-avatar">
            {user.username.slice(0, 2).toUpperCase()}
          </span>
          <span className="topbar__user-meta">
            <span className="topbar__user-name">{user.username}</span>
            <span className="topbar__user-role">
              {formatAccountTypeLabel(user.account_type)}
            </span>
          </span>
          <svg
            className="topbar__user-chev"
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {userMenuOpen && (
          <div className="topbar__user-menu">
            <Link
              to={`/users/${user.id}`}
              className="topbar__user-menu-item"
              onClick={() => setUserMenuOpen(false)}
            >
              <PilotsIcon />
              <span>My profile</span>
            </Link>
            <Link
              to="/flights/upload"
              className="topbar__user-menu-item"
              onClick={() => setUserMenuOpen(false)}
            >
              <UploadIcon />
              <span>Upload flight</span>
            </Link>
            <button
              type="button"
              className="topbar__user-menu-item"
              onClick={() => {
                setUserMenuOpen(false);
                void logout();
              }}
            >
              <LogoutIcon />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
